import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Flame, ArrowUpRight, Sparkles } from "lucide-react";

export default function Wall() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/wall/top-hooks`)
      .then((r) => r.json())
      .then((d) => setHooks(d.hooks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-void text-white">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold">V</div>
          <div>
            <div className="heading-display text-lg leading-none">VIRAL/CO</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">CONTENT MACHINE</div>
          </div>
        </Link>
        <Link
          to="/auth"
          data-testid="wall-cta-top"
          className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2.5 hover:bg-signal-yellow-hover transition-colors"
        >
          Start free →
        </Link>
      </header>

      <section className="px-6 md:px-12 lg:px-20 py-12 md:py-16 border-b border-white/10">
        <div className="max-w-4xl">
          <div className="label-eyebrow flex items-center gap-2">
            <Flame size={12} className="text-signal-yellow" /> // wall of 90+
          </div>
          <h1 className="heading-display text-4xl sm:text-6xl mt-3">
            HOOKS THAT SCORED <span className="text-signal-yellow">90+</span>.
          </h1>
          <p className="font-mono text-xs sm:text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            // real user-submitted content, rated 90+ by our virality engine. anonymized. use them for inspiration — or generate your own in seconds.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-10">
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 animate-pulse">// loading the wall...</div>
        ) : hooks.length === 0 ? (
          <div className="brutal-border p-12 bg-[#141414] text-center max-w-2xl mx-auto space-y-4" data-testid="wall-empty">
            <div className="font-mono text-xs text-zinc-500">// no 90+ hooks yet. be the first.</div>
            <Link
              to="/auth"
              data-testid="wall-cta-empty"
              className="inline-block bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-3 hover:bg-signal-yellow-hover transition-colors"
            >
              Generate your first hook →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wall-grid">
            {hooks.map((h, i) => (
              <article key={i} data-testid={`wall-hook-${i}`} className="brutal-border bg-[#141414] p-5 space-y-3 hover:border-signal-yellow transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{h.platform}</span>
                  <span className="font-mono text-lg font-bold text-signal-yellow">{h.score}/100</span>
                </div>
                <p className="font-mono text-sm text-zinc-100 leading-relaxed line-clamp-6">{h.content}</p>
                <Link
                  to="/auth"
                  data-testid={`wall-cta-${i}`}
                  className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-signal-yellow hover:underline"
                >
                  <Sparkles size={11} /> Generate one like this <ArrowUpRight size={11} />
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h2 className="heading-display text-3xl sm:text-5xl max-w-xl">WANT ON THIS WALL?</h2>
          <p className="font-mono text-xs text-zinc-500 mt-3 tracking-wider uppercase">// score a hook 90+ and it lands here (anonymized)</p>
        </div>
        <Link
          to="/auth"
          data-testid="wall-cta-bottom"
          className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors"
        >
          Start free →
        </Link>
      </section>

      <footer className="px-6 md:px-12 lg:px-20 py-8 border-t border-white/10 font-mono text-[10px] text-zinc-600 tracking-[0.2em] uppercase flex justify-between">
        <span>© VIRAL/CO — viral-co-hq.com — 2026</span>
        <Link to="/support" className="hover:text-signal-yellow transition-colors">Support</Link>
      </footer>
    </div>
  );
}
